import { useState, useEffect, useCallback, useRef, useId } from "react";
import { LoaderCircle, Plus, X } from "lucide-react";
import API_BASE_URL from "../config";
import HatchSwatch from "./HatchSwatch";
import ImageLightbox, { useImageLightbox } from "./ImageLightbox";
import "./LegendReviewCard.css";

// Minimum drag distance (px, on the rendered crop image) for a drawn rect to
// count as an intentional swatch box rather than an accidental click.
const MIN_DRAW_PX = 8;

let rowKeySeq = 0;
function nextRowKey() {
  rowKeySeq += 1;
  return `row-${rowKeySeq}`;
}

function toDraftRow(entry) {
  return { ...entry, _key: nextRowKey() };
}

// Fractional rect (0..1 of the crop image) -> PDF points, using the crop's
// known bbox origin. The crop is rendered as an exact clip of legend_bbox_pts
// (see render_legend_region), so a fraction of the DISPLAYED image maps
// linearly to the same fraction of the bbox rect regardless of the image's
// on-screen CSS size or natural pixel dimensions -- no DPI arithmetic needed.
function fracRectToPdfPoints(fracRect, legendBboxPts) {
  const [bx0, by0, bx1, by1] = legendBboxPts;
  const bw = bx1 - bx0;
  const bh = by1 - by0;
  return [
    bx0 + fracRect.x0 * bw,
    by0 + fracRect.y0 * bh,
    bx0 + fracRect.x1 * bw,
    by0 + fracRect.y1 * bh,
  ];
}

/**
 * Legend review/edit card -- used in two modes:
 *
 * - mode="edit" (default): shown once a takeoff job has fully completed.
 *   Mirrors the per-floor wall HITL editor's philosophy (auto-detect, then
 *   let the user review/correct after the fact): the CV row detector can
 *   pair unrelated text with an empty region and invent a false legend row
 *   (or, more rarely, miss a real one). Saving commits every edit as ONE
 *   batched recompute that reclassifies every already-extracted wall
 *   against the corrected legend -- never a per-row request.
 * - mode="approve": shown after Phase 1 (legend + floor heights, fast) of
 *   the legend-approval-gated flow finishes, BEFORE Phase 2 (the expensive
 *   per-wall CV+VLM classification) has run at all -- so the user reviews/
 *   edits the SAME legend, but there is nothing to reclassify yet.
 *   Confirming calls POST .../legend/confirm instead of .../legend/recompute
 *   and enqueues Phase 2; the caller (onConfirmed) hands control back to the
 *   main extraction progress stream, not this card's own recompute-status
 *   stream (which only exists for the post-hoc "edit" path).
 */
export default function LegendReviewCard({ jobId, onRecomputed, mode = "edit", onConfirmed }) {
  const isApproveMode = mode === "approve";
  // Column headers double as the visible labels of every row's inputs.
  const titleColId = useId();
  const canonicalColId = useId();
  const [entries, setEntries] = useState(null); // null = not loaded yet
  const [draft, setDraft] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnqueuing, setIsEnqueuing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [addRowOpen, setAddRowOpen] = useState(false);
  const { lightbox, openLightbox, closeLightbox, stepLightbox } = useImageLightbox();
  // Live recompute progress: {state: "running"|"done"|"error"|"cancelled",
  // message, floor_index, floor_count} | null. Populated either by starting
  // a new recompute or, on mount, by resuming one already in flight (e.g.
  // the page was reloaded mid-run) -- recompute_state.json is the durable
  // source of truth, so a reload never loses visibility into a still-running
  // recompute the way the old blocking-request design did.
  const [recomputeStatus, setRecomputeStatus] = useState(null);
  const eventSourceRef = useRef(null);
  const recomputeRunning = recomputeStatus?.state === "running";

  const closeRecomputeStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => closeRecomputeStream, [closeRecomputeStream]);

  const connectRecomputeStream = useCallback(() => {
    closeRecomputeStream();
    const es = new EventSource(
      `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/legend/recompute/stream`,
      { withCredentials: true },
    );
    eventSourceRef.current = es;
    es.onmessage = (ev) => {
      let data;
      try {
        data = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (data.type === "heartbeat") return;
      setRecomputeStatus(data);
      if (data.type === "done") {
        closeRecomputeStream();
        const fresh = data.entries || [];
        setEntries(fresh);
        setDraft(fresh.map(toDraftRow));
        if (onRecomputed) onRecomputed(data);
      } else if (data.type === "error" || data.type === "cancelled") {
        closeRecomputeStream();
        if (data.type === "error") setError(data.message || "Legend recompute failed");
      }
    };
    // EventSource auto-retries transient drops on its own; if the browser
    // eventually gives up, the durable status in recompute_state.json is
    // never lost -- reopening this card (fetchLegend) resumes it.
    es.onerror = () => {};
  }, [jobId, closeRecomputeStream, onRecomputed]);

  const fetchLegend = useCallback(async () => {
    if (!jobId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/legend`,
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Failed to fetch legend");
      }
      const data = await res.json();
      const fresh = data.entries || [];
      setEntries(fresh);
      setDraft(fresh.map(toDraftRow));
      if (data.legend_recompute_status?.state === "running") {
        setRecomputeStatus(data.legend_recompute_status);
        connectRecomputeStream();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [jobId, connectRecomputeStream]);

  useEffect(() => {
    fetchLegend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const isDirty =
    entries !== null &&
    (draft.length !== entries.length ||
      draft.some((row, i) => {
        const orig = entries[i];
        return (
          !orig ||
          row.raw_name !== orig.raw_name ||
          (row.canonical || "") !== (orig.canonical || "")
        );
      }));

  const handleRenameRow = (key, value) => {
    setDraft((prev) => prev.map((r) => (r._key === key ? { ...r, raw_name: value } : r)));
  };

  const handleCanonicalChange = (key, value) => {
    setDraft((prev) => prev.map((r) => (r._key === key ? { ...r, canonical: value } : r)));
  };

  const handleDeleteRow = (key) => {
    setDraft((prev) => prev.filter((r) => r._key !== key));
  };

  const handleAddRow = (newRow) => {
    setDraft((prev) => [...prev, toDraftRow(newRow)]);
    setAddRowOpen(false);
  };

  const handleSave = async () => {
    if (!jobId) return;
    setIsEnqueuing(true);
    setError(null);
    try {
      const payload = {
        entries: draft
          .filter((r) => r.raw_name && r.raw_name.trim())
          .map((r) => ({
            raw_name: r.raw_name.trim(),
            canonical: r.canonical || null,
            hatch_description: r.hatch_description || null,
            hatch_features: r.hatch_features || null,
            swatch_rect_pts: r.swatch_rect_pts || null,
            swatch_image_b64: r.swatch_image_b64 || null,
          })),
      };
      const endpoint = isApproveMode ? "legend/confirm" : "legend/recompute";
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(
          detail.detail ||
            (isApproveMode ? "Failed to confirm the legend" : "Legend recompute failed to start"),
        );
      }
      if (isApproveMode) {
        // Phase 2 is now enqueued -- the parent switches away from this card
        // entirely (back to the main extraction stream), not this card's own
        // recompute-status stream, which only applies to the post-hoc path.
        if (onConfirmed) onConfirmed();
        return;
      }
      // The request only enqueues now -- it returns immediately with a
      // task_id, well before any floor is actually reclassified. Live
      // progress arrives over the SSE stream, not this response.
      setRecomputeStatus({ state: "running", message: "Queued…", floor_index: 0, floor_count: null });
      connectRecomputeStream();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsEnqueuing(false);
    }
  };

  const handleCancelRecompute = async () => {
    if (!jobId) return;
    setIsCancelling(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/legend/recompute/cancel`,
        { method: "POST" },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Failed to cancel the recompute");
      }
      // The stream reports the actual "cancelled" terminal event once the
      // worker unwinds -- this just requests it.
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCancelling(false);
    }
  };

  if (!jobId) return null;

  return (
    <section className="ds-card lrc-card">
      <div className="lrc-header">
        <h3 className="ds-section-title ds-flush">
          {isApproveMode ? "Review legend before extracting walls" : "Legend"}
        </h3>
        <div className="lrc-header-actions">
          {recomputeRunning ? (
            <button
              type="button"
              className="ds-btn ds-btn--danger ds-btn--sm"
              onClick={handleCancelRecompute}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling…" : "Cancel"}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="ds-btn ds-btn--secondary ds-btn--sm"
                onClick={() => setAddRowOpen(true)}
                disabled={isLoading || isEnqueuing}
              >
                <Plus size={12} aria-hidden="true" />
                Add row
              </button>
              <button
                type="button"
                className="ds-btn ds-btn--primary ds-btn--sm"
                onClick={handleSave}
                disabled={isLoading || isEnqueuing || (!isApproveMode && !isDirty)}
                title={
                  isApproveMode
                    ? "Start wall extraction using this legend"
                    : !isDirty
                      ? "No unsaved legend edits"
                      : "Recompute every floor against these changes"
                }
              >
                {isEnqueuing && <LoaderCircle size={12} className="ds-spin" aria-hidden="true" />}
                {isEnqueuing
                  ? "Starting…"
                  : isApproveMode
                    ? "Confirm & start wall extraction"
                    : "Save & recompute"}
              </button>
            </>
          )}
        </div>
      </div>

      {recomputeStatus && recomputeStatus.state === "running" && (
        <div className="ds-alert ds-alert--info ds-flush" role="status">
          <LoaderCircle size={14} className="ds-spin" aria-hidden="true" />
          <span>
            {recomputeStatus.message || "Reclassifying…"}
            {recomputeStatus.floor_count
              ? ` (${recomputeStatus.floor_index}/${recomputeStatus.floor_count})`
              : ""}
          </span>
        </div>
      )}
      {recomputeStatus && recomputeStatus.state === "cancelled" && (
        <div className="ds-alert ds-alert--warning ds-flush">
          Recompute cancelled — no changes were saved.
        </div>
      )}

      {error && (
        <div className="ds-alert ds-alert--danger ds-flush" role="alert">
          {error}
        </div>
      )}

      {isLoading && !entries ? (
        <div className="ds-empty lrc-empty" role="status">
          Loading legend…
        </div>
      ) : draft.length === 0 ? (
        <div className="ds-empty lrc-empty">
          No legend rows — use "Add row" to draw one on the plan's legend box.
        </div>
      ) : (
        <div className="lrc-rows">
          <div className="lrc-row lrc-row--head">
            <span className="lrc-swatch-slot" aria-hidden="true" />
            <span id={titleColId} className="ds-label lrc-col-title">
              Legend title
            </span>
            <span id={canonicalColId} className="ds-label lrc-col-canonical">
              Canonical material
            </span>
            <span className="lrc-delete-slot" aria-hidden="true" />
          </div>
          {draft.map((row) => (
            <div key={row._key} className="lrc-row">
              {row.swatch_image_b64 ? (
                <button
                  type="button"
                  className="lrc-swatch-zoom"
                  onClick={() =>
                    openLightbox(
                      [{
                        url: `data:image/png;base64,${row.swatch_image_b64}`,
                        label: row.raw_name,
                      }],
                      0,
                    )
                  }
                  aria-label={`Enlarge swatch for ${row.raw_name}`}
                  title="Click to enlarge"
                >
                  <HatchSwatch
                    color={row.color}
                    hatchFeatures={row.hatch_features}
                    imageB64={row.swatch_image_b64}
                    size={28}
                  />
                </button>
              ) : (
                <HatchSwatch
                  color={row.color}
                  hatchFeatures={row.hatch_features}
                  imageB64={row.swatch_image_b64}
                  size={28}
                />
              )}
              <input
                type="text"
                className="ds-input ds-input--sm lrc-col-title"
                value={row.raw_name}
                onChange={(e) => handleRenameRow(row._key, e.target.value)}
                placeholder="Legend title"
                aria-labelledby={titleColId}
                disabled={recomputeRunning}
              />
              <input
                type="text"
                className="ds-input ds-input--sm lrc-col-canonical"
                aria-labelledby={canonicalColId}
                value={row.canonical || ""}
                onChange={(e) => handleCanonicalChange(row._key, e.target.value)}
                placeholder="auto"
                title="Canonical material (mauerwerk, stahlbeton, betonfertigteil, …) — leave blank to auto-detect from the title"
                list="lrc-canonical-suggestions"
                disabled={recomputeRunning}
              />
              <button
                type="button"
                className="ds-btn ds-btn--danger ds-btn--sm lrc-row-delete"
                onClick={() => handleDeleteRow(row._key)}
                aria-label={`Delete legend row ${row.raw_name}`}
                title="Delete this row"
                disabled={recomputeRunning}
              >
                <X size={12} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      <datalist id="lrc-canonical-suggestions">
        <option value="mauerwerk" />
        <option value="stahlbeton" />
        <option value="betonfertigteil" />
        <option value="unbekannt" />
      </datalist>

      {isApproveMode ? (
        <div className="ds-alert ds-alert--warning ds-flush">
          Wall extraction hasn't started yet — review the detected legend above (rename,
          delete, or add a row for any material CV missed), then confirm to begin.
        </div>
      ) : (
        isDirty && (
          <div className="ds-alert ds-alert--warning ds-flush">
            Unsaved legend changes — click "Save & recompute" to reclassify every floor's
            walls against them.
          </div>
        )
      )}

      {addRowOpen && (
        <AddLegendRowModal
          jobId={jobId}
          onCancel={() => setAddRowOpen(false)}
          onAdd={handleAddRow}
        />
      )}
      <ImageLightbox lightbox={lightbox} onClose={closeLightbox} onStep={stepLightbox} />
    </section>
  );
}

function AddLegendRowModal({ jobId, onCancel, onAdd }) {
  const [crop, setCrop] = useState(null); // {image_b64, legend_bbox_pts, render_dpi}
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragStart, setDragStart] = useState(null); // {x,y} fractional, 0..1
  const [dragRect, setDragRect] = useState(null); // {x0,y0,x1,y1} fractional
  const [title, setTitle] = useState("");
  const imgRef = useRef(null);
  const headingId = useId();
  const titleInputId = useId();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/legend/crop`,
        );
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail.detail || "Failed to load legend region");
        }
        const data = await res.json();
        if (!cancelled) setCrop(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const fracFromEvent = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    return { x, y };
  };

  const handlePointerDown = (e) => {
    if (!imgRef.current) return;
    e.preventDefault();
    const start = fracFromEvent(e);
    setDragStart(start);
    setDragRect({ x0: start.x, y0: start.y, x1: start.x, y1: start.y });
  };

  const handlePointerMove = (e) => {
    if (!dragStart || !imgRef.current) return;
    const cur = fracFromEvent(e);
    setDragRect({
      x0: Math.min(dragStart.x, cur.x),
      y0: Math.min(dragStart.y, cur.y),
      x1: Math.max(dragStart.x, cur.x),
      y1: Math.max(dragStart.y, cur.y),
    });
  };

  const handlePointerUp = () => {
    setDragStart(null);
  };

  const hasValidRect =
    dragRect &&
    imgRef.current &&
    (dragRect.x1 - dragRect.x0) * imgRef.current.getBoundingClientRect().width >= MIN_DRAW_PX &&
    (dragRect.y1 - dragRect.y0) * imgRef.current.getBoundingClientRect().height >= MIN_DRAW_PX;

  const handleConfirm = () => {
    if (!hasValidRect || !title.trim() || !crop) return;
    const swatchRectPts = fracRectToPdfPoints(dragRect, crop.legend_bbox_pts);
    onAdd({
      raw_name: title.trim(),
      canonical: null,
      hatch_description: "",
      hatch_features: null,
      swatch_rect_pts: swatchRectPts,
      swatch_image_b64: null,
    });
  };

  return (
    <div
      className="lrc-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      onClick={onCancel}
    >
      <div className="lrc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lrc-modal-header">
          <h3 id={headingId} className="ds-section-title ds-flush">
            Draw a box around the new material's pattern
          </h3>
          <button
            type="button"
            className="ds-btn ds-btn--ghost ds-btn--sm"
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        {isLoading && (
          <div className="ds-empty lrc-empty" role="status">
            Loading legend region…
          </div>
        )}
        {error && (
          <div className="ds-alert ds-alert--danger ds-flush" role="alert">
            {error}
          </div>
        )}

        {crop && (
          <>
            <div
              className="lrc-draw-surface"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              <img
                ref={imgRef}
                src={`data:image/png;base64,${crop.image_b64}`}
                alt="Legend region"
                className="lrc-draw-img"
                draggable={false}
              />
              {dragRect && (
                <div
                  className="lrc-draw-rect"
                  style={{
                    left: `${dragRect.x0 * 100}%`,
                    top: `${dragRect.y0 * 100}%`,
                    width: `${(dragRect.x1 - dragRect.x0) * 100}%`,
                    height: `${(dragRect.y1 - dragRect.y0) * 100}%`,
                  }}
                />
              )}
            </div>
            <div className="ds-help">
              Click and drag over the pattern/fill you want this material to match.
            </div>
            <div className="ds-field">
              <label htmlFor={titleInputId}>Material title</label>
              <input
                id={titleInputId}
                type="text"
                className="ds-input"
                placeholder="e.g. Wärmedämmung"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="lrc-modal-actions">
              <button type="button" className="ds-btn ds-btn--secondary" onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="ds-btn ds-btn--primary"
                onClick={handleConfirm}
                disabled={!hasValidRect || !title.trim()}
              >
                Add row
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
